import { Component, forwardRef, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidatorFn,
  FormsModule,
} from '@angular/forms';
import { FormValidation } from '@core/services/form-validation';
import { COUNTRY_CODES, CountryCode } from '@core/constants/country-codes.constants';

@Component({
  selector: 'app-mobile-number',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './mobile-number.html',
  styleUrls: ['./mobile-number.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MobileNumber),
      multi: true,
    },
  ],
})
export class MobileNumber implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() formControlName: string = '';
  @Input() control: AbstractControl | null = new FormControl();
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  value: string = '';
  disabled = false;
  countryCode: string = '+91';
  countries: CountryCode[] = COUNTRY_CODES;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    public formValidation: FormValidation,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.control) {
      this.control.addValidators(this.countryValidator());
      this.control.updateValueAndValidity();
    }
  }

  writeValue(value: any): void {
    if (!value) {
      this.value = '';
      return;
    }
    // Find the country code that matches start of value
    const matched = this.countries.find((c) => value.startsWith(c.dialCode));
    if (matched) {
      this.countryCode = matched.dialCode;
      this.value = value.slice(matched.dialCode.length).replace(/\D/g, '');
    } else {
      this.value = value.replace(/\D/g, '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.value = input.replace(/\D/g, ''); // Only digits
    this.emitValue();
  }
  // Prevent typing non-numeric characters
  allowOnlyNumbers(event: KeyboardEvent) {
    const key = event.key;
    if (!/[\d]/.test(key)) {
      event.preventDefault();
    }
  }

  // Handle paste event - extract only numbers and optionally detect country code
  allowOnlyPasteNumbers(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    
    if (!pasteData) return;
    
    // Get current input element and selection
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = this.value;
    
    // Extract all digits from pasted content
    const allDigits = pasteData.replace(/\D/g, '');
    
    if (!allDigits) return;
    
    // Try to find matching country code
    let matchedCountry: CountryCode | undefined;
    let numberPart = allDigits;
    
    // First, check if paste data includes the full dial code with + sign (most reliable)
    const sortedCountries = [...this.countries].sort((a, b) => 
      b.dialCode.replace(/\D/g, '').length - a.dialCode.replace(/\D/g, '').length
    );
    
    for (const country of sortedCountries) {
      if (pasteData.includes(country.dialCode)) {
        matchedCountry = country;
        numberPart = pasteData
          .replace(country.dialCode, '')
          .replace(/\D/g, '');
        break;
      }
    }
    
    // If no match with + sign, check if pasted digits start with country dial code (without +)
    if (!matchedCountry) {
      for (const country of sortedCountries) {
        const dialCodeDigits = country.dialCode.replace(/\D/g, '');
        // Only match if the dial code is at the start and the remaining digits are reasonable length
        if (allDigits.startsWith(dialCodeDigits)) {
          const remainingDigits = allDigits.slice(dialCodeDigits.length);
          // Check if remaining digits match the expected length range for that country
          if (remainingDigits.length >= country.minLength && remainingDigits.length <= country.maxLength) {
            matchedCountry = country;
            numberPart = remainingDigits;
            break;
          }
        }
      }
    }
    
    // Update country code if matched
    if (matchedCountry) {
      this.countryCode = matchedCountry.dialCode;
      // Limit the number part to maxLength
      this.value = numberPart.slice(0, matchedCountry.maxLength);
    } else {
      // No country code found, insert pasted digits at cursor position or replace selection
      const beforeCursor = currentValue.slice(0, start);
      const afterCursor = currentValue.slice(end);
      const newValue = beforeCursor + allDigits + afterCursor;
      // Limit to max length of current country if available
      const currentCountry = this.countries.find((c) => c.dialCode === this.countryCode);
      if (currentCountry) {
        this.value = newValue.slice(0, currentCountry.maxLength);
      } else {
        this.value = newValue;
      }
    }
    
    // Trigger change detection to update the view (including country dropdown)
    this.cdr.detectChanges();
    
    // Set cursor position after the pasted content
    setTimeout(() => {
      if (input) {
        const newCursorPos = matchedCountry 
          ? this.value.length 
          : Math.min(start + allDigits.length, this.value.length);
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }
    }, 0);
    
    this.emitValue();
  }

  onCountryChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.countryCode = select.value;
    this.emitValue();
  }

  private emitValue() {
    this.onChange(this.countryCode + this.value);
    this.onTouched();
    this.control?.updateValueAndValidity({ onlySelf: true });
  }

  get isInvalid(): boolean {
    return !!(this.control?.touched && this.control?.invalid);
  }

  get required(): boolean {
    if (!this.control || !this.control.validator) return false;
    const validator = this.control.validator({} as AbstractControl);
    return !!validator?.['required'];
  }

  get errorMessage(): string | null {
    if (!this.control || !this.control.touched || this.control.valid) return null;
    if (this.control.errors?.['invalidMobile']) {
      return this.control.errors['invalidMobile'].message;
    }
    return this.formValidation.getValidationMessage(this.control, this.formControlName);
  }

  private countryValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) return null;

      // Find selected country
      const country = this.countries.find((c) => c.dialCode === this.countryCode);
      if (!country) return null;

      // Remove country code and all non-digits
      let number = control.value.toString().replace(/\D/g, '');
      if (number.startsWith(country.dialCode.replace(/\D/g, ''))) {
        number = number.slice(country.dialCode.replace(/\D/g, '').length);
      }

      // Validate against country regex
      const isValid = country.regex.test(number);

      return isValid
        ? null
        : {
            invalidMobile: {
              country: country.code,
              message: `Please enter a valid mobile number for ${country.code} (${country.dialCode})`,
            },
          };
    };
  }
}
